import React, { useEffect, useState, useRef } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Home, Package, ClipboardList, Wallet, User as UserIcon, Bell, LogOut, LifeBuoy, ShieldCheck, IndianRupee, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import A2HSBanner from "@/components/A2HSBanner";

const BOTTOM_NAV = [
  { to: "/merchant", label: "Home", icon: Home, end: true },
  { to: "/merchant/services", label: "Services", icon: Package },
  { to: "/merchant/leads", label: "Leads", icon: ClipboardList },
  { to: "/merchant/wallet", label: "Wallet", icon: Wallet },
  { to: "/merchant/profile", label: "Profile", icon: UserIcon },
];

const PILL_NAV = [
  ...BOTTOM_NAV,
  { to: "/merchant/make-payment", label: "Make Payment", icon: IndianRupee },
  { to: "/merchant/kyc", label: "KYC", icon: ShieldCheck },
  { to: "/merchant/support", label: "Support", icon: LifeBuoy },
];

export default function MerchantLayout({ title, subtitle, children, hideHeader = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const menuRef = useRef(null);
  const notifRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    api.get("/notifications/active").then((r) => setNotifs(r.data || [])).catch(() => {});
  }, [location.pathname]);

  // close popovers on outside click
  useEffect(() => {
    const h = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
  };

  const unreadNotifs = notifs.length;

  return (
    <div className="merchant-root">
      {/* Header */}
      <header className="m-header">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center gap-3">
          <div className="flex items-center gap-2.5">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-purple-700 text-white flex items-center justify-center font-bold text-lg shadow-md">
              E
            </div>
            <div className="leading-tight">
              <div className="font-display font-extrabold text-slate-900 text-base">EPAYCASH</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Merchant</div>
            </div>
          </div>

          {/* Desktop pills */}
          <nav className="hidden lg:flex items-center gap-1.5 ml-6 flex-1">
            {PILL_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) => `m-pill ${isActive ? "active" : ""}`}
                data-testid={`m-pill-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <n.icon className="h-3.5 w-3.5" />
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex-1 lg:hidden" />

          {/* Notification bell */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => { setNotifOpen((v) => !v); setMenuOpen(false); }}
              className="m-tap h-10 w-10 rounded-full bg-white border border-indigo-100 text-indigo-700 flex items-center justify-center hover:bg-indigo-50 transition relative"
              data-testid="m-bell-btn"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5" />
              {unreadNotifs > 0 && (
                <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid="m-bell-count">
                  {unreadNotifs > 9 ? "9+" : unreadNotifs}
                </span>
              )}
            </button>
            {notifOpen && (
              <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden" data-testid="m-notif-panel">
                <div className="px-4 py-3 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
                  <div className="font-semibold text-slate-900">Notifications</div>
                  <div className="text-xs text-slate-500">{unreadNotifs} active</div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifs.length === 0 && <div className="p-6 text-center text-sm text-slate-500">कोई notification नहीं है</div>}
                  {notifs.map((n) => (
                    <div key={n.id} className="px-4 py-3 border-b last:border-b-0 hover:bg-slate-50">
                      <div className="font-medium text-sm text-slate-900">{n.title}</div>
                      {n.message && <div className="text-xs text-slate-600 mt-0.5">{n.message}</div>}
                      {n.cta_url && n.cta_label && (
                        <a href={n.cta_url} target="_blank" rel="noreferrer" className="text-xs text-indigo-600 font-semibold mt-1 inline-flex items-center gap-1">
                          {n.cta_label} <ChevronRight className="h-3 w-3" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Profile avatar */}
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => { setMenuOpen((v) => !v); setNotifOpen(false); }}
              className="m-tap h-10 w-10 rounded-full overflow-hidden ring-2 ring-indigo-100 hover:ring-indigo-300 transition"
              data-testid="m-avatar-btn"
              aria-label="Profile menu"
            >
              {user?.photo_url
                ? <img src={user.photo_url} alt="me" className="w-full h-full object-cover" />
                : <div className="w-full h-full bg-gradient-to-br from-indigo-600 to-purple-600 text-white flex items-center justify-center font-bold text-sm">{(user?.name || "M")[0].toUpperCase()}</div>}
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden" data-testid="m-avatar-menu">
                <div className="px-4 py-3 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
                  <div className="font-semibold text-slate-900 truncate">{user?.name || "—"}</div>
                  <div className="text-xs text-slate-500 font-mono">{user?.mobile}</div>
                </div>
                <button onClick={() => { setMenuOpen(false); navigate("/merchant/profile"); }} className="w-full px-4 py-2.5 text-sm text-left hover:bg-slate-50 flex items-center gap-2.5" data-testid="m-menu-profile">
                  <UserIcon className="h-4 w-4 text-slate-500" /> My Profile
                </button>
                <button onClick={() => { setMenuOpen(false); navigate("/merchant/kyc"); }} className="w-full px-4 py-2.5 text-sm text-left hover:bg-slate-50 flex items-center gap-2.5" data-testid="m-menu-kyc">
                  <ShieldCheck className="h-4 w-4 text-slate-500" /> KYC
                </button>
                <button onClick={() => { setMenuOpen(false); navigate("/merchant/support"); }} className="w-full px-4 py-2.5 text-sm text-left hover:bg-slate-50 flex items-center gap-2.5" data-testid="m-menu-support">
                  <LifeBuoy className="h-4 w-4 text-slate-500" /> Support
                </button>
                <button onClick={logout} className="w-full px-4 py-2.5 text-sm text-left hover:bg-rose-50 text-rose-600 flex items-center gap-2.5 border-t" data-testid="m-menu-logout">
                  <LogOut className="h-4 w-4" /> Logout / लॉग आउट
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="m-shell">
        {!hideHeader && (title || subtitle) && (
          <div className="mb-4">
            {title && <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight">{title}</h1>}
            {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
          </div>
        )}
        <A2HSBanner />
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="m-bottom-nav lg:hidden">
        <div className="max-w-md mx-auto flex items-center">
          {BOTTOM_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) => `m-bottom-nav-item m-tap ${isActive ? "active" : ""}`}
              data-testid={`m-bnav-${n.label.toLowerCase()}`}
            >
              <n.icon className="h-5 w-5" strokeWidth={2.2} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

/**
 * Greeting hook — returns time-of-day based greeting
 */
export function useGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}
